/**
 * Main Product Page — variant/image gallery, zoom, modal, add-to-cart.
 */

class ProductPage {
  constructor() {
    this.container = document.querySelector('.product-page');
    if (!this.container) return;

    this.initData();
    this.initTabs();
    this.initVariantSelection();
    this.initVariantPreload();
    this.initImageSlider();
    this.initImageModal();
    this.initZoom();
    this.initTrendingBadge();
    this.initAddToCart();
    this.initQuantity();
    this.initStickyAtc();
    this.initAccessibility();
  }

  initData() {
    this.productVariants = window.productVariants || [];
    this.variantInventory = window.variantInventory || {};
    this.formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    this._preloadedVariants = new Set();
  }

  initAccessibility() {
    document.querySelectorAll('.thumbnail').forEach(thumb => {
      thumb.setAttribute('tabindex', '0');
      thumb.setAttribute('role', 'button');
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          thumb.click();
        }
      });
    });
  }

  initTrendingBadge() {
    const badgeCount = document.getElementById('psc-top-badge-count');
    const productId = this.container.dataset.productId;
    if (!badgeCount || !productId) return;

    const hashCode = (str) => {
      let hash = 0;
      for (let i = 0, len = str.length; i < len; i++) {
        let chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0;
      }
      return Math.abs(hash);
    };

    const today = new Date();
    const dateString = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
    const seedNumber = hashCode(productId + '_' + dateString);
    const DAILY_MIN = 14, DAILY_MAX = 89;

    badgeCount.innerText = DAILY_MIN + (seedNumber % (DAILY_MAX - DAILY_MIN + 1));
  }

  initTabs() {
    const btns = document.querySelectorAll('.tab-btn, .accordion-btn');
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tab;
        const isActive = btn.classList.contains('active');
        if (btn.classList.contains('accordion-btn') && isActive) {
          this.toggleTab(target, false);
        } else {
          this.toggleTab(target, true);
        }
      });
    });
  }

  toggleTab(id, show) {
    const tabBtns = document.querySelectorAll('.tab-btn');
    const accBtns = document.querySelectorAll('.accordion-btn');
    const panels = document.querySelectorAll('.tab-panel');

    tabBtns.forEach(b => b.classList.toggle('active', b.dataset.tab === id && show));
    accBtns.forEach(b => {
      const isMatch = b.dataset.tab === id && show;
      b.classList.toggle('active', isMatch);
      const icon = b.querySelector('.acc-icon');
      if (icon) icon.textContent = isMatch ? '−' : '+';
    });
    panels.forEach(p => p.classList.toggle('active', p.id === id && show));
  }

  initVariantSelection() {
    document.querySelectorAll('.variant-radio').forEach(r => {
      r.addEventListener('change', () => this.updateVariant());
    });
    // Sync UI state with the server-rendered selected variant on load.
    this.updateVariant();
  }

  updateVariant() {
    const selectedOptions = Array.from({ length: 3 }, (_, i) => {
      const checked = document.querySelector(`.variant-radio[data-option-position="${i + 1}"]:checked`);
      return checked ? checked.value : null;
    }).filter(v => v);

    const match = this.productVariants.find(v =>
      v.options.every((opt, idx) => opt === selectedOptions[idx])
    );
    if (!match) { this.updatePillAvailability(); return; }

    const hidden = document.getElementById('variant-id');
    if (hidden) hidden.value = match.id;

    this.updatePrice(match);
    this.updateAvailability(match);
    this.updateStockMessage(match);

    // Scroll the gallery to the variant's featured image. All thumbs and slides
    // stay in the DOM; only the .active class changes.
    const featId = match.featured_media_id;
    if (featId) {
      this.scrollToImage(featId);
    } else {
      const first = document.querySelector('.main-image-slide');
      if (first) this.scrollToImage(first.dataset.imageId);
    }

    this.updatePillAvailability();
  }

  updatePrice(variant) {
    const price = this.formatter.format(variant.price / 100);
    ['productPrice', 'productPriceMobile'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.textContent = price;
    });

    const compare = document.getElementById('productComparePrice');
    if (compare) {
      if (variant.compare_at_price > variant.price) {
        compare.textContent = this.formatter.format(variant.compare_at_price / 100);
        compare.style.display = 'inline';
      } else {
        compare.style.display = 'none';
      }
    }
  }

  updateAvailability(variant) {
    const btn = document.querySelector('.btn-add-to-cart');
    const text = document.getElementById('addToCartText');
    const badge = document.querySelector('.trending-image-badge');
    const stickyBtn = document.querySelector('[data-sticky-atc-btn]');
    const stickyPrice = document.getElementById('stickyAtcPrice');

    if (!btn || !text) return;
    const i18nAdd = btn.dataset.i18nAdd || 'ADD TO ORDER';
    const i18nSoldOut = btn.dataset.i18nSoldOut || 'SOLD OUT';
    if (variant.available) {
      text.textContent = i18nAdd;
      btn.disabled = false;
      if (badge) badge.style.opacity = '1';
    } else {
      text.textContent = i18nSoldOut;
      btn.disabled = true;
      if (badge) badge.style.opacity = '0.5';
    }
    if (stickyBtn) {
      const sa = stickyBtn.dataset.i18nAdd || i18nAdd;
      const ss = stickyBtn.dataset.i18nSoldOut || i18nSoldOut;
      stickyBtn.textContent = variant.available ? sa : ss;
      stickyBtn.disabled = !variant.available;
    }
    if (stickyPrice) stickyPrice.textContent = this.formatter.format(variant.price / 100);
  }

  updateStockMessage(variant) {
    const el = document.getElementById('variant-stock-message');
    if (!el) return;

    // i18n strings (with __COUNT__ placeholder for low-stock) passed from Liquid.
    const container = el.closest('.scarcity-container');
    const i18nIn = (container && container.dataset.i18nInStock) || '● In Stock | 10+ Stocks Left';
    const i18nLow = (container && container.dataset.i18nLowStock) || '● High Demand | Only __COUNT__ Left in Stock';
    const i18nOut = (container && container.dataset.i18nOutOfStock) || '● Out of Stock';

    const qty = this.variantInventory[variant.id] || 0;
    const fill = document.getElementById('stock-progress-fill');
    if (!variant.available) {
      el.textContent = i18nOut;
      el.className = 'urgent-stock-message sold-out';
      if (fill) { fill.style.width = '0%'; fill.className = 'stock-progress-fill low-stock'; }
    } else if (qty > 0 && qty < 10) {
      el.textContent = i18nLow.replace(/__COUNT__/g, String(qty));
      el.className = 'urgent-stock-message low-stock';
      if (fill) { fill.style.width = (qty * 10) + '%'; fill.className = 'stock-progress-fill low-stock'; }
    } else {
      el.textContent = i18nIn;
      el.className = 'urgent-stock-message in-stock';
      if (fill) { fill.style.width = '100%'; fill.className = 'stock-progress-fill'; }
    }
  }

  updatePillAvailability() {
    document.querySelectorAll('.variant-radio').forEach(r => {
      const pos = parseInt(r.dataset.optionPosition, 10);
      const test = Array.from({ length: 3 }, (_, i) => {
        if (i + 1 === pos) return r.value;
        const checked = document.querySelector(`.variant-radio[data-option-position="${i + 1}"]:checked`);
        if (checked) return checked.value;
        const fallback = document.querySelector(`.variant-radio[data-option-position="${i + 1}"]`);
        return fallback ? fallback.value : null;
      }).filter(v => v !== null);

      const match = this.productVariants.find(v => v.options.every((opt, idx) => opt === test[idx]));
      const label = document.querySelector(`label[for="${r.id}"]`);
      if (label) label.classList.toggle('sold-out-pill', match && !match.available);
    });
  }

  initImageSlider() {
    document.querySelectorAll('.thumbnail').forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.scrollToImage(thumb.dataset.imageId);
        const vIds = (thumb.dataset.variantIds || '').split(',').filter(Boolean);
        if (vIds.length > 0) this.selectVariantById(vIds[0]);
      });
    });
  }

  scrollToImage(imageId) {
    if (!imageId) return;
    const needle = String(imageId);
    const matched = document.querySelector(`.main-image-slide[data-image-id="${needle}"]`);
    if (matched) {
      document.querySelectorAll('.main-image-slide').forEach(s => s.classList.remove('active'));
      matched.classList.add('active');

      // Reset zoom on swap (old active may be mid-zoom).
      const img = matched.querySelector('img');
      if (img) img.style.transform = 'scale(1)';
    }
    document.querySelectorAll('.thumbnail').forEach(t => {
      t.classList.toggle('active', t.dataset.imageId === needle);
    });
  }

  selectVariantById(id) {
    const variant = this.productVariants.find(v => String(v.id) === String(id));
    const hidden = document.getElementById('variant-id');
    if (!variant || (hidden && String(hidden.value) === String(id))) return;

    variant.options.forEach((val, idx) => {
      document
        .querySelectorAll(`.variant-radio[data-option-position="${idx + 1}"]`)
        .forEach(r => { if (r.value === val) r.checked = true; });
    });

    if (hidden) hidden.value = id;
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateStockMessage(variant);
    this.updatePillAvailability();
  }

  /* ------------------------------------------------------------------
   * Preload the featured image for the variant a user is about to pick.
   * Fired on pill hover or keyboard focus so the swap is instant.
   * ------------------------------------------------------------------ */
  initVariantPreload() {
    document.querySelectorAll('.variant-pill-label').forEach(label => {
      const radio = document.getElementById(label.getAttribute('for'));
      if (!radio) return;
      const preload = () => this._preloadForRadio(radio);
      label.addEventListener('mouseenter', preload, { passive: true });
      radio.addEventListener('focus', preload, { passive: true });
    });
  }

  _preloadForRadio(radio) {
    const pos = parseInt(radio.dataset.optionPosition, 10);
    if (!pos) return;
    const candidateOpts = Array.from({ length: 3 }, (_, i) => {
      if (i + 1 === pos) return radio.value;
      const checked = document.querySelector(`.variant-radio[data-option-position="${i + 1}"]:checked`);
      return checked ? checked.value : null;
    }).filter(v => v !== null);

    const target = this.productVariants.find(v =>
      v.options.every((opt, idx) => opt === candidateOpts[idx])
    );
    if (!target || this._preloadedVariants.has(target.id)) return;

    const slide = this._findSlideForVariant(target.id);
    if (!slide) return;
    const img = slide.querySelector('img');
    if (!img) return;

    this._preloadedVariants.add(target.id);

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = img.src;
    if (img.srcset) link.imageSrcset = img.srcset;
    if (img.sizes) link.imageSizes = img.sizes;
    document.head.appendChild(link);
  }

  _findSlideForVariant(variantId) {
    const needle = String(variantId);
    return Array.from(document.querySelectorAll('.main-image-slide')).find(slide => {
      const ids = (slide.dataset.variantIds || '').split(',').filter(Boolean);
      return ids.includes(needle);
    });
  }

  /* ------------------------------------------------------------------
   * Desktop hover-zoom on the active main image, rAF-throttled.
   * ------------------------------------------------------------------ */
  initZoom() {
    if (window.innerWidth <= 768) return;

    this._zoomState = { slide: null, img: null, rect: null, rafPending: false, lastX: 0, lastY: 0 };
    const state = this._zoomState;

    const onEnter = (e) => {
      const slide = e.currentTarget;
      const img = slide.querySelector('img');
      if (!img) return;
      state.slide = slide;
      state.img = img;
      state.rect = slide.getBoundingClientRect();
    };
    const onMove = (e) => {
      if (!state.img || !state.rect) return;
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      if (state.rafPending) return;
      state.rafPending = true;
      requestAnimationFrame(() => {
        state.rafPending = false;
        if (!state.img || !state.rect) return;
        const { left, top, width, height } = state.rect;
        const px = Math.max(0, Math.min(100, ((state.lastX - left) / width) * 100));
        const py = Math.max(0, Math.min(100, ((state.lastY - top) / height) * 100));
        state.img.style.transformOrigin = `${px}% ${py}%`;
        state.img.style.transform = 'scale(1.5)';
      });
    };
    const onLeave = (e) => {
      const img = e.currentTarget.querySelector('img');
      if (img) img.style.transform = 'scale(1)';
      if (state.slide === e.currentTarget) {
        state.slide = null;
        state.img = null;
        state.rect = null;
      }
    };

    document.querySelectorAll('.main-image-slide').forEach(slide => {
      slide.addEventListener('mouseenter', onEnter);
      slide.addEventListener('mousemove', onMove, { passive: true });
      slide.addEventListener('mouseleave', onLeave);
    });

    window.addEventListener('resize', () => {
      if (state.slide) state.rect = state.slide.getBoundingClientRect();
    }, { passive: true });
  }

  /* ------------------------------------------------------------------
   * Full-screen image modal with keyboard + click delegation.
   * Uses img.currentSrc so the modal honors the browser-picked srcset.
   * ------------------------------------------------------------------ */
  initImageModal() {
    this.modal = document.getElementById('productImageModal');
    this.modalImg = document.getElementById('imgModalContent');
    if (!this.modal) return;

    const openFromImg = (img) => {
      if (!img) return;
      this.modal.style.display = 'flex';
      this.modal.setAttribute('aria-hidden', 'false');
      this.modalImg.src = img.currentSrc || img.src;
      this.modalImg.alt = img.alt || '';
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      this.modal.style.display = 'none';
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    // Backwards-compat global hooks in case other code still calls them.
    window.openImageModal = (src) => {
      if (typeof src === 'string' && src) {
        this.modal.style.display = 'flex';
        this.modal.setAttribute('aria-hidden', 'false');
        this.modalImg.src = src;
        document.body.style.overflow = 'hidden';
      }
    };
    window.closeImageModal = close;

    // Main slides → click/keyboard open modal
    document.querySelectorAll('.main-image-slide').forEach(slide => {
      const activate = () => openFromImg(slide.querySelector('img'));
      slide.addEventListener('click', activate);
      slide.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
      });
    });

    // Lifestyle gallery (delegated — images may be absent)
    document.querySelectorAll('[data-modal-trigger]').forEach(group => {
      group.addEventListener('click', (e) => {
        const img = e.target.closest('img');
        if (img) openFromImg(img);
      });
    });

    // Modal controls (delegated)
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal || e.target.classList.contains('close-modal')) {
        close();
        return;
      }
      const nav = e.target.closest('[data-modal-nav]');
      if (nav) this.navigateModal(parseInt(nav.dataset.modalNav, 10));
    });

    document.addEventListener('keydown', (e) => {
      if (this.modal.style.display !== 'flex') return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') this.navigateModal(1);
      else if (e.key === 'ArrowLeft') this.navigateModal(-1);
    });
  }

  navigateModal(direction) {
    const imgs = Array.from(
      document.querySelectorAll('.main-image-slide img')
    );
    if (imgs.length <= 1) return;
    const current = this.modalImg.src;
    let idx = imgs.findIndex(img => (img.currentSrc || img.src) === current);
    if (idx === -1) idx = 0;
    idx = (idx + direction + imgs.length) % imgs.length;
    const next = imgs[idx];
    this.modalImg.src = next.currentSrc || next.src;
    this.modalImg.alt = next.alt || '';
  }

  initQuantity() {
    const input = document.getElementById('quantity');
    if (!input) return;
    document.querySelectorAll('.qty-btn[data-qty]').forEach(btn => {
      btn.addEventListener('click', () => {
        const delta = parseInt(btn.dataset.qty, 10) || 0;
        const current = parseInt(input.value, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        input.value = Math.max(min, current + delta);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ------------------------------------------------------------------
   * Mobile-only sticky ATC bar.
   * Appears once the main .btn-add-to-cart scrolls out of view.
   * Clicks delegate to the main form's submit so one code path handles cart add.
   * ------------------------------------------------------------------ */
  initStickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    const mainBtn = document.querySelector('.btn-add-to-cart');
    if (!bar || !mainBtn || !('IntersectionObserver' in window)) return;

    const mql = window.matchMedia('(max-width: 768px)');
    let observer = null;

    const show = () => {
      bar.hidden = false;
      bar.setAttribute('aria-hidden', 'false');
      // next frame for the transform transition to fire
      requestAnimationFrame(() => bar.classList.add('is-visible'));
    };
    const hide = () => {
      bar.classList.remove('is-visible');
      bar.setAttribute('aria-hidden', 'true');
      setTimeout(() => { bar.hidden = true; }, 250);
    };

    const start = () => {
      if (observer) return;
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) hide();
          else show();
        });
      }, { threshold: 0 });
      observer.observe(mainBtn);
    };
    const stop = () => {
      if (observer) { observer.disconnect(); observer = null; }
      hide();
    };

    const sync = () => { mql.matches ? start() : stop(); };

    sync();
    mql.addEventListener('change', sync);

    // Sticky button → submit the main form (single source of truth for cart add)
    const stickyBtn = bar.querySelector('[data-sticky-atc-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = document.getElementById('add-to-cart-form');
        if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    }
  }

  initAddToCart() {
    const form = document.getElementById('add-to-cart-form');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = form.querySelector('button[type="submit"]');
      const text = document.getElementById('addToCartText');
      const original = text ? text.textContent : '';
      const adding = btn && btn.dataset.i18nAdding ? btn.dataset.i18nAdding : 'ADDING…';
      if (text) text.textContent = adding;
      if (btn) btn.disabled = true;
      fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
        .then(r => r.json())
        .then(item => {
          if (item.status) throw new Error(item.description);
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(err => { console.error(err); alert('Problem adding to cart.'); })
        .finally(() => {
          if (text) text.textContent = original;
          if (btn) btn.disabled = false;
        });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => { window.productPage = new ProductPage(); });
